import React, { useRef, useEffect, useState } from 'react'
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Heading2,
  Heading3,
  Italic,
  Link2,
  List,
  ListOrdered,
  Minus,
  Image as ImageIcon,
  Quote,
  Redo2,
  Strikethrough,
  Underline,
  Undo2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface HTMLEditorProps {
  id?: string
  value?: string
  onChange?: (html: string) => void
  placeholder?: string
  rows?: number
  className?: string
  disabled?: boolean
}

export const HTMLEditor: React.FC<HTMLEditorProps> = ({
  id,
  value = '',
  onChange,
  placeholder = '',
  rows = 4,
  className,
  disabled = false,
}) => {
  const editorRef = useRef<HTMLDivElement>(null)
  const [_isActive, setIsActive] = useState(false)
  const isInitializedRef = useRef(false)

  // Initialize editor content on mount, then sync when parent value changes
  // (e.g. async edit load). Skip sync while the user is typing.
  useEffect(() => {
    if (!editorRef.current) {
      return
    }
    if (!isInitializedRef.current) {
      editorRef.current.innerHTML = value || ''
      isInitializedRef.current = true
      return
    }
    if (document.activeElement === editorRef.current) {
      return
    }
    const current = editorRef.current.innerHTML
    const next = value || ''
    if (current !== next) {
      editorRef.current.innerHTML = next
    }
  }, [value])

  const handleInput = () => {
    if (editorRef.current && onChange) {
      onChange(editorRef.current.innerHTML)
    }
  }

  const applyStyle = (command: string, commandValue?: string) => {
    editorRef.current?.focus()
    document.execCommand(command, false, commandValue)
    handleInput()
  }

  const toggleList = (ordered: boolean) => {
    applyStyle(ordered ? 'insertOrderedList' : 'insertUnorderedList')
  }

  const handleInsertLink = () => {
    const selection = window.getSelection()?.toString() || ''
    const url = window.prompt('Enter link URL (https://...)', 'https://')
    if (!url) {
      return
    }
    if (selection) {
      applyStyle('createLink', url)
      return
    }
    const label = window.prompt('Link text', url) || url
    applyStyle('insertHTML', `<a href="${url}" target="_blank" rel="noopener noreferrer">${label}</a>`)
  }

  const handleInsertImage = () => {
    const url = window.prompt('Enter image URL (https://...)')
    if (!url) {
      return
    }
    applyStyle('insertImage', url)
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault()
    const text = e.clipboardData.getData('text/html') || e.clipboardData.getData('text/plain')
    document.execCommand('insertHTML', false, text)
    handleInput()
  }

  return (
    <div className={cn('border rounded-md overflow-hidden bg-white', disabled && 'opacity-50 pointer-events-none')}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 border-b bg-gray-50 p-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => applyStyle('undo')}
          title="Undo"
          className="h-8 w-8 p-0"
        >
          <Undo2 className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => applyStyle('redo')}
          title="Redo"
          className="h-8 w-8 p-0"
        >
          <Redo2 className="h-4 w-4" />
        </Button>

        <div className="mx-0.5 h-5 w-px bg-gray-300" />

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => applyStyle('formatBlock', 'h2')}
          title="Heading"
          className="h-8 w-8 p-0"
        >
          <Heading2 className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => applyStyle('formatBlock', 'h3')}
          title="Subheading"
          className="h-8 w-8 p-0"
        >
          <Heading3 className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => applyStyle('formatBlock', 'p')}
          title="Paragraph"
          className="h-8 px-2 text-xs font-semibold"
        >
          P
        </Button>

        <div className="mx-0.5 h-5 w-px bg-gray-300" />

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => applyStyle('bold')}
          title="Bold (Ctrl+B)"
          className="h-8 w-8 p-0"
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => applyStyle('italic')}
          title="Italic (Ctrl+I)"
          className="h-8 w-8 p-0"
        >
          <Italic className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => applyStyle('underline')}
          title="Underline (Ctrl+U)"
          className="h-8 w-8 p-0"
        >
          <Underline className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => applyStyle('strikeThrough')}
          title="Strikethrough"
          className="h-8 w-8 p-0"
        >
          <Strikethrough className="h-4 w-4" />
        </Button>

        <div className="mx-0.5 h-5 w-px bg-gray-300" />

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => applyStyle('justifyLeft')}
          title="Align left"
          className="h-8 w-8 p-0"
        >
          <AlignLeft className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => applyStyle('justifyCenter')}
          title="Align center"
          className="h-8 w-8 p-0"
        >
          <AlignCenter className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => applyStyle('justifyRight')}
          title="Align right"
          className="h-8 w-8 p-0"
        >
          <AlignRight className="h-4 w-4" />
        </Button>

        <div className="mx-0.5 h-5 w-px bg-gray-300" />

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => toggleList(false)}
          title="Bullet list"
          className="h-8 w-8 p-0"
        >
          <List className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => toggleList(true)}
          title="Numbered list"
          className="h-8 w-8 p-0"
        >
          <ListOrdered className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => applyStyle('formatBlock', 'blockquote')}
          title="Quote"
          className="h-8 w-8 p-0"
        >
          <Quote className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => applyStyle('insertHorizontalRule')}
          title="Divider"
          className="h-8 w-8 p-0"
        >
          <Minus className="h-4 w-4" />
        </Button>

        <div className="mx-0.5 h-5 w-px bg-gray-300" />

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleInsertLink}
          title="Insert link"
          className="h-8 w-8 p-0"
        >
          <Link2 className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleInsertImage}
          title="Insert image"
          className="h-8 w-8 p-0"
        >
          <ImageIcon className="h-4 w-4" />
        </Button>
        <div className="mx-0.5 h-5 w-px bg-gray-300" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => applyStyle('removeFormat')}
          title="Clear formatting"
          className="h-8 w-8 p-0 text-xs"
        >
          ✕
        </Button>
      </div>

      {/* Content Area */}
      <div
        ref={editorRef}
        id={id}
        contentEditable={!disabled}
        onInput={handleInput}
        onPaste={handlePaste}
        onFocus={() => setIsActive(true)}
        onBlur={() => setIsActive(false)}
        suppressContentEditableWarning
        className={cn(
          'min-h-24 p-3 prose prose-sm max-w-none focus:outline-none',
          'text-sm text-foreground',
          'overflow-y-auto',
          '[&:empty]:before:content-[attr(data-placeholder)] [&:empty]:before:text-muted-foreground',
          className,
        )}
        style={{ minHeight: `${(rows ?? 4) * 1.5}rem` }}
        data-placeholder={placeholder}
      />
    </div>
  )
}
